// NEW (not in TP): a local Collapsible with the Root / Trigger / Content API of `@radix-ui/react-collapsible`,
// which TP's `ui/shadcn/collapsible.tsx` re-exports. @radix-ui/react-collapsible is not a dependency of this
// app and none may be added (plan P3-4), so ported markup such as
//   <Collapsible open={isOpen} onOpenChange={setOpen}><CollapsibleTrigger asChild>...</CollapsibleTrigger>
//   <CollapsibleContent>...</CollapsibleContent></Collapsible>
// keeps working 1:1 against this file.
//
// Contract (the subset TP uses, plus Radix's accessibility wiring):
//   - Root: controlled (`open` + `onOpenChange`) or uncontrolled (`defaultOpen`); `disabled`; a <div> with
//     `data-state="open|closed"` and any other div props (e.g. `data-finding`).
//   - Trigger: a <button type="button"> (or the child element with `asChild`) with `aria-expanded`,
//     `aria-controls` -> the content id, `data-state`; click toggles unless the handler prevents default.
//   - Content: a <div id> with `data-state`. When closed it is `hidden` and its children are not mounted
//     (as Radix unmounts them), so `aria-controls` always points at a real element. `forceMount` keeps the
//     children mounted (still `hidden` while closed).
//   - The content's measured height is exposed as `--radix-collapsible-content-height`, so the
//     `animate-collapsible-down` keyframe in tailwind.config.ts works on it. There is no exit animation:
//     closing is instant, and `motion-reduce` consumers skip the opening one too.
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

interface CollapsibleContextValue {
  open: boolean;
  disabled: boolean;
  contentId: string;
  toggle: () => void;
}

const CollapsibleContext = React.createContext<CollapsibleContextValue | null>(null);

function useCollapsibleContext(part: string): CollapsibleContextValue {
  const ctx = React.useContext(CollapsibleContext);
  if (!ctx) throw new Error(`${part} must be used within <Collapsible>`);
  return ctx;
}

const stateOf = (open: boolean) => (open ? 'open' : 'closed');

export interface CollapsibleProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'defaultChecked'> {
  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean;
  /** Initial state when uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}

const Collapsible = React.forwardRef<HTMLDivElement, CollapsibleProps>(function Collapsible(
  { open: openProp, defaultOpen = false, onOpenChange, disabled = false, children, ...props },
  ref,
) {
  const contentId = React.useId();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const toggle = React.useCallback(() => {
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [open, isControlled, onOpenChange]);

  const value = React.useMemo(() => ({ open, disabled, contentId, toggle }), [open, disabled, contentId, toggle]);

  return (
    <CollapsibleContext.Provider value={value}>
      <div ref={ref} data-state={stateOf(open)} data-disabled={disabled ? '' : undefined} {...props}>
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
});

export interface CollapsibleTriggerProps extends React.ComponentPropsWithoutRef<'button'> {
  /** Render the child element as the trigger (it receives the props and the ref). */
  asChild?: boolean;
}

const CollapsibleTrigger = React.forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(function CollapsibleTrigger(
  { asChild = false, onClick, disabled, ...props },
  ref,
) {
  const ctx = useCollapsibleContext('CollapsibleTrigger');
  const isDisabled = ctx.disabled || disabled;
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      {...(asChild ? {} : { type: 'button' as const })}
      aria-controls={ctx.contentId}
      aria-expanded={ctx.open}
      data-state={stateOf(ctx.open)}
      data-disabled={isDisabled ? '' : undefined}
      disabled={isDisabled}
      {...props}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (!event.defaultPrevented) ctx.toggle();
      }}
    />
  );
});

export interface CollapsibleContentProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Keep the children mounted while closed (the content stays `hidden`). */
  forceMount?: boolean;
}

const CollapsibleContent = React.forwardRef<HTMLDivElement, CollapsibleContentProps>(function CollapsibleContent(
  { forceMount = false, children, style, ...props },
  forwardedRef,
) {
  const ctx = useCollapsibleContext('CollapsibleContent');
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState<number | undefined>(undefined);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  // Measure on open so `animate-collapsible-down` can run from 0 to the real height.
  React.useLayoutEffect(() => {
    if (ctx.open && innerRef.current) setHeight(innerRef.current.scrollHeight);
  }, [ctx.open]);

  const measured = height === undefined ? undefined : ({ '--radix-collapsible-content-height': `${height}px` } as React.CSSProperties);

  return (
    <div
      ref={setRefs}
      id={ctx.contentId}
      data-state={stateOf(ctx.open)}
      data-disabled={ctx.disabled ? '' : undefined}
      hidden={!ctx.open}
      style={{ ...measured, ...style }}
      {...props}
    >
      {ctx.open || forceMount ? children : null}
    </div>
  );
});

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
